import { Component } from '@theme/component';
import { oncePerEditorSession } from '@theme/utilities';

export class ComparisonSliderComponent extends Component {
  requiredRefs = ['mediaWrapper', 'slider', 'afterImage'];

  constructor() {
    super();

    this.hasAnimated = false;
    this.boundHandleIntersection = this.handleIntersection.bind(this);

    // link config
    this.linkTimeout = null;
    this.linkLeft = null;
    this.linkRight = null;
    this.linkDelay = 500;

    // drag state
    this.isDragging = false;
    this.activePointerId = null;

    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    const { mediaWrapper, slider } = this.refs;

    this.orientation = mediaWrapper.dataset.orientation || 'horizontal';

    // prevent page scroll + mobile freeze fix
    mediaWrapper.style.touchAction = 'none';

    this.sync();
    this.readConfig();

    this.setupEvents();
    this.setupIntersectionObserver();
  }

  readConfig() {
    this.linkLeft = this.getAttribute('data-link-left');
    this.linkRight = this.getAttribute('data-link-right');
    this.linkDelay = parseInt(this.getAttribute('data-link-delay') || 500, 10);
  }

  setupEvents() {
    const { slider } = this.refs;

    // input = live update
    slider.addEventListener('input', () => this.sync());

    // start drag
    slider.addEventListener('pointerdown', (e) => {
      this.isDragging = true;
      this.activePointerId = e.pointerId;

      slider.setPointerCapture?.(e.pointerId);

      window.addEventListener('pointermove', this.handlePointerMove);
      window.addEventListener('pointerup', this.handlePointerUp, { once: true });
    });
  }

  handlePointerMove() {
    this.sync();
  }

  handlePointerUp() {
    this.isDragging = false;

    window.removeEventListener('pointermove', this.handlePointerMove);

    const value = this.getCompareValue();
    this.triggerLink(value);
  }

  sync() {
    const { mediaWrapper, slider } = this.refs;
    if (this.isAnimating) return;

    const value = this.getCompareValue();

    mediaWrapper.style.setProperty('--compare', String(value));

    // KEIN redirect während drag → verhindert scroll bugs
  }

  getCompareValue() {
    const { slider } = this.refs;

    const val =
      (Number(slider.value) - Number(slider.min)) /
      (Number(slider.max) - Number(slider.min));

    return Math.round(val * 100);
  }

  triggerLink(value) {
    clearTimeout(this.linkTimeout);

    const isLeftZone = value <= 3;
    const isRightZone = value >= 97;

    if (isLeftZone && this.linkLeft) {
      this.linkTimeout = setTimeout(() => {
        window.location.href = this.linkLeft;
      }, this.linkDelay);
    }

    if (isRightZone && this.linkRight) {
      this.linkTimeout = setTimeout(() => {
        window.location.href = this.linkRight;
      }, this.linkDelay);
    }
  }

  setValue(value) {
    const { slider } = this.refs;
    if (!slider) return;

    slider.value = String(value);
    this.sync();
  }

  animateSlider() {
    const { mediaWrapper, slider } = this.refs;

    if (this.hasAnimated) return;

    this.hasAnimated = true;
    this.isAnimating = true;

    mediaWrapper.style.setProperty('--transition-duration', '0.5s');

    setTimeout(() => mediaWrapper.style.setProperty('--compare', '40'), 100);
    setTimeout(() => mediaWrapper.style.setProperty('--compare', '60'), 600);
    setTimeout(() => mediaWrapper.style.setProperty('--compare', '50'), 1100);

    setTimeout(() => {
      mediaWrapper.style.setProperty('--transition-duration', '0s');
      slider.value = '50';
      this.isAnimating = false;
    }, 1600);
  }

  setupIntersectionObserver() {
    if (!window.IntersectionObserver) return;

    this.intersectionObserver = new IntersectionObserver(
      this.boundHandleIntersection,
      { root: null, threshold: 0.5 }
    );

    this.intersectionObserver.observe(this);
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.hasAnimated) {
        setTimeout(() => {
          oncePerEditorSession(this, 'comparison-slider-animated', () => {
            this.animateSlider();
          });
        }, 300);

        this.intersectionObserver?.disconnect();
      }
    });
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    clearTimeout(this.linkTimeout);
  }
}

if (!customElements.get('comparison-slider-component')) {
  customElements.define('comparison-slider-component', ComparisonSliderComponent);
}